import { RootState } from '../../../store';
// eslint-disable-next-line import/no-unresolved
import Solr from '../../../solr';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ListColumnMetadata, ListProps } from '../Search/List';
import * as H from 'history';
import FacettedSearchView, {
  FacettedSearchViewProps,
} from './FacettedSearchView';
import {
  createSolrCollection,
  getSolrCollections,
  getSolrResult,
  setSolrSearch,
  setSolrTypeParams,
  toggleSolrFacet,
  updateSolrResult,
} from '../../../actions';
import qs from 'query-string';
import { facetMetadata } from '../Search/FacetFilter';
import * as TableHeader from '../Search/headerComponents';
import * as TableContent from '../Search/contentComponents';
import { Dispatch } from 'redux';
import { Plug } from '@plone/volto/components/manage/Pluggable';
import { Icon } from '@plone/volto/components';
import responsibilitiesSVG from '../../../icons/responsibilities.svg';

export type SolrColumnMetadata = Omit<
  ListColumnMetadata,
  'sortable' | 'sorted' | 'HeaderComponent' | 'CellComponent' | 'title'
> & {
  sortable?: true;
};

export type SolrContentTypeData<Category extends Solr.Category> = {
  id: Category;
  reviewStates: Array<{
    id: Solr.ResultState[Category];
    title: string;
  }>;
  columns: Array<
    Omit<ListColumnMetadata, 'sortable' | 'title'> & {
      sortable?: true;
      // Solr field to sort on if it is not the column id, Solr can not sort
      // on a tokenized text field like Title
      sortOn?: string;
    }
  >;
};

export type SolrContentTypePostData<Category extends Solr.Category> = {
  id: Category;
  reviewStates: Array<{
    id: Solr.ResultState[Category];
    title: string;
  }>;
  columns: SolrColumnMetadata[];
};

const contentTypes: SolrContentTypeData<Solr.Category>[] = [
  {
    id: 'Wiki',
    reviewStates: [
      {
        id: 'verified',
        title: 'Verifiziert',
      },
      {
        id: 'discussion',
        title: 'Zur Diskussion',
      },
      {
        id: 'release_control',
        title: 'Zur Freigabe',
      },
      {
        id: 'in_work',
        title: 'In Bearbeitung',
      },
    ],
    columns: [
      {
        id: 'Title',
        label: 'Artikel',
        sortable: true,
        sortOn: 'sortable_title',
        HeaderComponent: TableHeader.Title,
        CellComponent: TableContent.Title,
      },
      {
        id: 'Description',
        label: 'Beschreibung',
      },
      {
        id: 'departments',
        label: 'Bereich',
        CellComponent: TableContent.Departments,
      },
      {
        id: 'modified',
        label: 'Änderungsdatum',
        sortable: true,
        CellComponent: TableContent.Modified,
      },
      {
        id: 'verified',
        label: 'Verifiziert',
        CellComponent: TableContent.Verified,
      },
    ],
  },
  {
    id: 'Medien',
    reviewStates: [
      {
        id: 'verified',
        title: 'Verifiziert',
      },
      {
        id: 'release_control',
        title: 'Zur Freigabe',
      },
      {
        id: 'in_work',
        title: 'In Bearbeitung',
      },
    ],
    columns: [
      {
        id: 'mimetype',
        label: '',
        HeaderComponent: TableHeader.MIMETypeTitle,
        CellComponent: TableContent.MIMEType,
      },
      {
        id: 'Title',
        label: 'Artikel',
        sortable: true,
        sortOn: 'sortable_title',
        HeaderComponent: TableHeader.TitleMedia,
        CellComponent: TableContent.Title,
      },
      {
        id: 'sid',
        label: 'ID',
      },
      {
        id: 'types',
        label: 'Typ',
        CellComponent: TableContent.Types,
      },
      {
        id: 'modified',
        label: 'Änderungsdatum',
        sortable: true,
        CellComponent: TableContent.Modified,
      },
      {
        id: 'Creator',
        label: 'Autor',
        CellComponent: TableContent.Creator,
      },
    ],
  },
];

const defaultBatchSize = 10;

// order of every list as long as no column header was clicked, with a search
// term Solr sorts by relevance instead
const defaultSortParams: Solr.TypeParams = {
  sort_on: 'sortable_title',
  sort_order: '',
};

const reviewStateColors: Record<Solr.ReviewState, string> = {
  verified: 'var(--horn-blue)',
  discussion: 'var(--horn-green)',
  release_control: 'var(--horn-pink)',
  in_work: 'var(--horn-yellow)',
};

const columnDataToSolrColumnData = (
  data: SolrContentTypeData<Solr.Category>['columns'],
): Array<{ id: string; sortable: boolean; sorted?: 'asc' | 'desc' }> =>
  data.map(({ id, sortable, sorted }) => {
    return {
      id,
      sortable: sortable ?? false,
      sorted: sorted || undefined,
    };
  });

const getSortOn = (
  column: SolrContentTypeData<Solr.Category>['columns'][number],
): string => column.sortOn ?? column.id;

// returns the same object if every list is sorted already, so the effect that
// depends on the typeParams in the store does not run again and again
export const withDefaultSort = (
  typeParams: NonNullable<RootState['solr']>['search']['typeParams'],
): NonNullable<RootState['solr']>['search']['typeParams'] => {
  let sortedTypeParams = typeParams;

  contentTypes.forEach(({ id, reviewStates }) => {
    reviewStates.forEach((reviewState) => {
      if (sortedTypeParams?.[id]?.[reviewState.id]?.sort_on) return;

      sortedTypeParams = {
        ...sortedTypeParams,
        [id]: {
          ...sortedTypeParams?.[id],
          [reviewState.id]: {
            ...sortedTypeParams?.[id]?.[reviewState.id],
            ...defaultSortParams,
          },
        },
      };
    });
  });

  return sortedTypeParams;
};

interface SearchData {
  collection_title: string;
  search: {
    SearchableText: string | null;
    query: {
      category: Solr.Category[];
    };
    facets: Partial<Record<Solr.Facets, string[]>>;
    typeParams: NonNullable<RootState['solr']>['search']['typeParams'];
    batch_size: number;
    uid: string;
  };
  contentTypes: SolrContentTypePostData<Solr.Category>[];
  facetedIndexes: Array<(typeof facetMetadata)['data'][number]['id']>;
}

export const getSearchData = (
  locationSearch: H.Location['search'],
  selectedFacets: Partial<Record<Solr.Facets, string[]>>,
  typeParams: NonNullable<RootState['solr']>['search']['typeParams'],
  facetedIndices: string[] = facetMetadata.data.map(({ id }) => id),
): SearchData => {
  const searchableText = qs.parse(locationSearch).SearchableText as string;
  const uid = qs.parse(locationSearch).uid as string;
  const department = qs.parse(locationSearch).department as string;
  const category = qs.parse(locationSearch).category as string;
  const categories = contentTypes.map(({ id }) => id);
  // a saved collection (uid) is searched with its own stored params
  const useDefaultSort = !searchableText && !uid;

  if (department) {
    const departments: Array<string> = [];
    departments.push(department);

    selectedFacets.departments = departments;
  } else if (category) {
    const selectedCategories: Array<string> = [];
    selectedCategories.push(category);

    selectedFacets.category = selectedCategories;
  }

  return {
    collection_title: '',
    search: {
      SearchableText: searchableText,
      query: { category: categories },
      facets: selectedFacets,
      typeParams: useDefaultSort ? withDefaultSort(typeParams) : typeParams,
      batch_size: defaultBatchSize,
      uid: uid,
    },
    contentTypes: contentTypes.map(
      <C extends Solr.Category>({
        id,
        reviewStates,
        columns,
      }: SolrContentTypeData<C>) => {
        return {
          id,
          reviewStates,
          columns: columns.map(({ id, label, sortable }) => {
            return { id, label, sortable };
          }),
        };
      },
    ),
    facetedIndexes: facetedIndices,
  };
};

export const createListSortFn = <C extends Solr.Category>(
  dispatch: Dispatch<any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  contentType: SolrContentTypeData<C>,
  reviewState: { id: Solr.ResultState[C] },
  typeParams: Solr.TypeParams | undefined,
  updateResults: (
    contentType: C,
    reviewState: Solr.ResultState[C],
    columnData: SolrContentTypeData<C>['columns'],
  ) => void,
): ListProps['sort'] => {
  return (columnId) => {
    const column = contentType.columns.find(
      ({ id }) => id === columnId,
    ) as SolrContentTypeData<C>['columns'][number];

    if (column.sortable !== true) return;

    const sortOn = getSortOn(column);
    const params: Partial<Solr.TypeParams> = {
      sort_on: typeParams?.sort_on,
    };
    if (sortOn === typeParams?.sort_on) {
      if (typeParams?.sort_order === 'reverse') params.sort_order = '';
      else params.sort_order = 'reverse';
    } else {
      params.sort_on = sortOn;
      params.sort_order = 'reverse';
    }

    dispatch(setSolrTypeParams(contentType.id, reviewState.id, params));
    updateResults(contentType.id, reviewState.id, contentType.columns);
  };
};

export interface SolrSearchViewProps {} // eslint-disable-line @typescript-eslint/no-empty-interface

const SolrSearchView: FC<SolrSearchViewProps> = () => {
  const dispatch = useDispatch();
  const [location, facets, selectedFacets, search, typeParams, results] =
    useSelector((state: RootState) => {
      return [
        state.router?.location as H.Location,
        state.solr?.result?.facets ?? {},
        state.solr?.search.facets ?? {},
        state.solr?.search ?? {},
        state.solr?.search?.typeParams ?? {},
        state.solr?.result?.results,
      ] as const;
    });

  const [createCollection, setCreateCollection] = useState(false);
  const [solrCollectionTitleinput, setSolrCollectionTitleinput] = useState('');
  const [, setHideFormClassName] = useState(false);

  const toggleFacet: FacettedSearchViewProps['toggleFacet'] = (
    facetId,
    facetValueId,
  ) => {
    dispatch(toggleSolrFacet(facetId, facetValueId));
  };

  const updateResults = <C extends Solr.Category>(
    contentType: C,
    reviewState: Solr.ResultState[C],
    columnData: SolrContentTypeData<C>['columns'],
  ): void => {
    dispatch(
      updateSolrResult({
        contentType,
        reviewState,
        search,
        columns: columnDataToSolrColumnData(columnData),
      }),
    );
  };

  const listGroups: FacettedSearchViewProps['listGroups'] = contentTypes.map(
    <C extends Solr.Category>(contentType: SolrContentTypeData<C>) => {
      return {
        title: contentType.id,
        lists: contentType.reviewStates.map((reviewState) => {
          const specificTypeParams = typeParams[contentType.id]?.[
            reviewState.id
          ] as Solr.TypeParams | undefined;
          const data = results?.[contentType.id][reviewState.id] ?? {
            actual_result_count: 0,
            items: [],
          };
          const sort: ListProps['sort'] = createListSortFn(
            dispatch,
            contentType,
            reviewState,
            specificTypeParams,
            updateResults,
          );

          const addBatchSize = () => {
            dispatch(
              setSolrTypeParams(contentType.id, reviewState.id, {
                batch_size:
                  (specificTypeParams?.batch_size ?? defaultBatchSize) +
                  defaultBatchSize,
              }),
            );
            updateResults(contentType.id, reviewState.id, contentType.columns);
          };

          const loadAll = () => {
            dispatch(
              setSolrTypeParams(contentType.id, reviewState.id, {
                batch_size: data.actual_result_count,
              }),
            );
            updateResults(contentType.id, reviewState.id, contentType.columns);
          };
          const columns: ListColumnMetadata[] = contentType.columns.map(
            (column) => {
              const specificTypeParams = typeParams[contentType.id]?.[
                reviewState.id
              ] as Solr.TypeParams | undefined;

              return {
                ...column,
                sortable: column.sortable ?? false,
                sorted:
                  specificTypeParams?.sort_on === getSortOn(column)
                    ? specificTypeParams?.sort_order === 'reverse'
                      ? 'desc'
                      : 'asc'
                    : false,
              };
            },
          );

          return {
            headerColor: reviewStateColors[reviewState.id],
            data: data.items.map(
              (item: { columns: ListColumnMetadata[]; url: string }) => {
                return { ...item.columns, url: item.url };
              },
            ),
            columns,
            sort,
            resultCount: data.actual_result_count,
            batchingProps: {
              batchSize: defaultBatchSize,
              addBatchSize,
              loadAll,
            },
            title: reviewState.title,
            listId: reviewState.id,
            wrapperClassName: 'type-' + contentType.id.toLowerCase(),
          } as ListProps;
        }),
      };
    },
  );

  useEffect(() => {
    const data = getSearchData(location.search, selectedFacets, typeParams);
    dispatch(setSolrSearch(data.search));
    dispatch(getSolrResult(data));

    if (createCollection) {
      data.collection_title = solrCollectionTitleinput;
      // The page is not reloaded any more, so the collections of the navbar
      // are fetched again once the new one is saved (#22806)
      (
        dispatch(createSolrCollection(data)) as unknown as Promise<unknown>
      ).then(() => dispatch(getSolrCollections()));
      setCreateCollection(false);
    }
  }, [
    dispatch,
    createCollection,
    setCreateCollection,
    solrCollectionTitleinput,
    location.search,
    selectedFacets,
    typeParams,
  ]);

  // reset solr search when SearchableText gets changed
  useEffect(() => {
    const data = getSearchData(location.search, {}, {});
    dispatch(setSolrSearch(data.search));
    dispatch(getSolrResult(data));
  }, [dispatch, location.search]);

  return (
    <>
      <Plug pluggable="horn.next.searchwidget" id="solrcollection-form">
        <div className={'solrcollection-wrapper'}>
          <Icon
            className={'solrcollection-open-form-button'}
            name={responsibilitiesSVG}
            size="18px"
            // @ts-ignore
            onClick={(e) =>
              setHideFormClassName(
                e.target.parentElement.classList.toggle('hide-form'),
              )
            }
          ></Icon>
          <form
            className={'solrcollection-form'}
            onSubmit={(e) => {
              // Saved by the effect above, the browser must not send the form
              // and reload the page, also not on Enter in the field (#22806)
              e.preventDefault();
              setCreateCollection(true);
            }}
          >
            <div className={'ui input'}>
              <input
                type="text"
                className={'ui input solrcollection-title-field'}
                placeholder={'Titel eingeben'}
                // @ts-ignore
                onInput={(e) => setSolrCollectionTitleinput(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className={'huh primary solrcollection-create-button'}
            >
              Kollektion erstellen
            </button>
          </form>
        </div>
      </Plug>
      <FacettedSearchView
        facets={facets}
        selectedFacets={selectedFacets}
        toggleFacet={toggleFacet}
        listGroups={listGroups}
      />
    </>
  );
};

export default Object.assign(SolrSearchView, {
  contentTypes,
  defaultBatchSize,
  defaultSortParams,
  reviewStateColors,
});
